import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, Building2, MapPin, FileText, Handshake } from "lucide-react";
import { globalReach, whyChoose, company } from "../mock";

const About = () => {
  return (
    <div>
      {/* Header */}
      <section className="relative bg-gradient-to-b from-teal-50/70 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24 text-center">
          <span className="inline-block bg-teal-100 text-teal-800 text-xs font-bold tracking-[0.12em] px-4 py-2 rounded-full">
            ABOUT US
          </span>
          <h1 className="mt-6 text-5xl font-extrabold tracking-tight text-slate-900">
            Bridging Borders, Building Trust
          </h1>
          <p className="mt-5 text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            {company.tagline}
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">
              Who We Are
            </h2>
            <p className="mt-5 text-[15px] text-slate-500 leading-relaxed">
              JLa Consultants LLC is a global consulting firm specializing in
              international trade and business development. With deep expertise
              across the US, India, and Latin America, we help companies
              navigate complex markets and unlock new opportunities.
            </p>
            <p className="mt-4 text-[15px] text-slate-500 leading-relaxed">
              From export/import of bulk pharmaceuticals and recyclable plastic
              resins to human capital development and partnership facilitation,
              our team brings decades of combined experience to every
              engagement.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-7 py-3.5 rounded-full transition-all duration-200 hover:shadow-lg hover:shadow-teal-600/25 active:scale-95"
            >
              Work With Us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex justify-center">
            <div className="relative w-72 h-72 lg:w-80 lg:h-80 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-teal-100/70" />
              <div className="absolute inset-8 rounded-full bg-teal-50" />
              <Globe
                className="relative w-36 h-36 lg:w-44 lg:h-44 text-teal-500"
                strokeWidth={1.2}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-center text-3xl font-extrabold tracking-tight text-slate-900">
            Our Values
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-7">
            {whyChoose.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white rounded-2xl p-7 text-center border border-slate-100 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                >
                  <div className="mx-auto flex items-center justify-center w-14 h-14 rounded-full bg-teal-100 text-teal-600">
                    <Icon className="w-7 h-7" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-slate-900">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Global Reach */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-900">
              Our Global Reach
            </h2>
            <p className="mt-4 text-lg text-slate-500">
              Operating across three continents to connect markets and create
              lasting partnerships.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-7">
            {globalReach.map((region) => (
              <div
                key={region.region}
                className="bg-white border border-slate-100 rounded-2xl p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                <h3 className="text-xl font-bold text-slate-900">
                  {region.region}
                </h3>
                <p className="mt-1 text-sm font-semibold text-teal-600">
                  {region.focus}
                </p>
                <p className="mt-4 text-sm text-slate-500 leading-relaxed">
                  {region.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offices & Partnerships */}
      <section className="py-16 lg:py-20 bg-slate-50 border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-900">
              Our Offices & Partnerships
            </h2>
            <p className="mt-4 text-lg text-slate-500">
              A registered global presence backed by strategic international
              partnerships.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-7">
            {/* India Office */}
            <div className="bg-white border border-slate-100 rounded-2xl p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-xl bg-slate-900 shrink-0 bg-no-repeat"
                  style={{
                    backgroundImage:
                      "url(https://customer-assets.emergentagent.com/job_media-manager-77/artifacts/6urxp0na_JLA%20Orion%20Private%20Limited.png)",
                    backgroundSize: "210%",
                    backgroundPosition: "11% 50%",
                  }}
                  role="img"
                  aria-label="JLA Orion Private Limited"
                />
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    India Office
                  </h3>
                  <p className="text-sm font-semibold text-teal-600">
                    JLA Orion Private Limited
                  </p>
                </div>
              </div>
              <div className="mt-6 space-y-3">
                <div className="flex items-start gap-3">
                  <FileText className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-slate-600">
                    Registration{" "}
                    <span className="font-semibold text-slate-800">
                      #215182
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Panama Partnership */}
            <div className="bg-white border border-slate-100 rounded-2xl p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
              <div className="flex items-center gap-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_media-manager-77/artifacts/iunwwl8o_Inlace.png"
                  alt="INLACE - Republic of Panama"
                  className="w-14 h-14 object-contain shrink-0"
                />
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    Strategic Partnership
                  </h3>
                  <p className="text-sm font-semibold text-teal-600">
                    INLACE — Panama Incorporated Company
                  </p>
                </div>
              </div>
              <div className="mt-6 space-y-3">
                <div className="flex items-start gap-3">
                  <FileText className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-slate-600">
                    RUC:{" "}
                    <span className="font-semibold text-slate-800">
                      155692705-2-2020
                    </span>
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-slate-600 leading-relaxed">
                    #20, Edificio PH Regente, Calle 52, Bella Vista, Panama
                    City, Republic of Panama
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
